import { type AppState, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { type ReactNode, useState } from 'react';
import { AuthError } from '@/common/components/error/AuthError';
import { getEnv } from '@/common/utils/getEnv';
import type { FileRoutesByTo } from '@/routeTree.gen';
import { UserLinkingStateContext } from './userLinkingStateContext';

const restrictNoAuthRoutes: ReadonlyArray<keyof FileRoutesByTo> = [
  '/organizations/start',
  '/organization-selector',
];

const AuthErrorHandler = ({ children }: { children: ReactNode }) => {
  const { error, isAuthenticated } = useAuth0();

  if (error && !isAuthenticated) {
    return <AuthError error={error} />;
  }

  return <>{children}</>;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { nexusRedirectUri, auth0Domain, auth0ClientId } = getEnv();
  const [appState, setAppState] = useState<AppState | undefined>();

  return (
    <Auth0Provider
      clientId={auth0ClientId}
      domain={auth0Domain}
      authorizationParams={{ redirect_uri: nexusRedirectUri }}
      skipRedirectCallback={
        /^\/integrations\/[^/]+\/callback$/.test(window.location.pathname) ||
        window.location.pathname.includes('/idp-login')
      }
      onRedirectCallback={(appState) => {
        setAppState(appState);
        // This is what auth0-react does internally, but we need to do it manually if defining a custom redirect callback.

        const isRouteDisallowed = restrictNoAuthRoutes.some((path) =>
          appState?.returnTo?.includes(path),
        );
        const returnTo = !isRouteDisallowed
          ? (appState?.returnTo ?? window.location.pathname)
          : window.location.origin;
        window.history.replaceState({}, document.title, returnTo);
      }}
    >
      <UserLinkingStateContext.Provider value={{ token: appState?.link_token }}>
        <AuthErrorHandler>{children}</AuthErrorHandler>
      </UserLinkingStateContext.Provider>
    </Auth0Provider>
  );
};
